import type { QueryRowType } from "@rocicorp/zero";
import { useState } from "react";
import { styled } from "goober";
import {
  normalizeRouteAuth,
  type RouteAuthMode,
} from "../../../shared/monitoringRouteAuth";
import { parseBranchRoutePath } from "../../../shared/oauth/resource";
import {
  spaceEdgeHostnameSuffix,
  type TenantEdgeUrlOptions,
} from "../../../shared/edgeUrl";
import { queries } from "../../../shared/queries";
import { draftRouteAuth } from "../../../shared/stepConfig";
import { useBranchServerReady } from "../../hooks/useBranchServerReady";
import { useQuery } from "@rocicorp/zero/react";
import { useSessionTenantEdgeUrlOptions } from "../../hooks/useSessionTenantOrigin";
import { useSetPresence } from "../../hooks/useSetPresence";
import { useTenantCustomDomain } from "../../hooks/useTenantCustomDomain";
import {
  routeAuthDescription,
  routeAuthLabel,
} from "../../lib/routeAuthDisplay";
import { getPageUrl } from "../../lib/stepUrl";
import { BrowserChrome } from "../BrowserChrome";
import { EmptyState } from "../EmptyState";
import { Icon, type IconName } from "../Icon";
import { Page } from "../LiveBrowser/Page";
import useAgentControl from "../LiveBrowser/useAgentControl";
import useAgentControlBridge from "../LiveBrowser/useAgentControlBridge";
import { useNavigation } from "../LiveBrowser/useNavigation";
import { Tooltip } from "../Tooltip";

export type WorkflowPageLink = {
  href: string;
  origin: string;
  route: string;
  branchId?: string;
  workflowId?: string;
  externalId: string | null;
};

const ROUTE_AUTH_ICONS: Record<RouteAuthMode, IconName> = {
  public: "earth",
  external_id: "link-2",
  tenant: "building-2",
  sso: "key",
  connected_app: "plug",
  space: "lock",
};

const AccessIcon = styled("span")`
  display: flex;
  flex-shrink: 0;
`;

type PageBranch = QueryRowType<ReturnType<typeof queries.workflowPageForRoute>>;

export function workflowPageLink(
  href: string,
  options: TenantEdgeUrlOptions
): WorkflowPageLink | null {
  if (!URL.canParse(href)) return null;
  const url = new URL(href);
  const suffix = spaceEdgeHostnameSuffix(options);
  if (
    url.host !== new URL(options.edgeUrl).host &&
    !(suffix && url.hostname.endsWith(suffix))
  )
    return null;
  const branch = parseBranchRoutePath(url.pathname);
  return {
    href,
    origin: url.origin,
    route: branch?.path ?? url.pathname,
    ...(branch && { branchId: branch.branchId }),
    externalId: url.searchParams.get("external_id"),
  };
}

function PagePreview({
  branch,
  link,
}: {
  branch: PageBranch;
  link: WorkflowPageLink;
}) {
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const customDomain = useTenantCustomDomain();
  const space = branch.workflow!.space!;
  const branchId = branch.name === "main" ? null : branch.id;
  const route = branch.headCommit?.routes[0];
  const externalId = link.externalId ?? route?.externalId ?? null;
  const liveAuth = normalizeRouteAuth(route?.routeAuth);
  const liveGroups = liveAuth === "sso" ? (route?.routeGroups ?? []) : [];
  const auth = branchId
    ? normalizeRouteAuth(draftRouteAuth(liveAuth))
    : liveAuth;
  const groups = auth === "sso" ? liveGroups : [];
  const navigation = useNavigation(link.route, branchId ?? undefined);
  const agentControl = useAgentControl();
  useAgentControlBridge(agentControl, route?.stepDir ?? "", true);
  useSetPresence(branch.workflowId, branchId ?? undefined);
  const responding = branch.headCommitChat?.respondingAt != null;
  const [turns, setTurns] = useState({ responding, settled: 0 });
  if (turns.responding !== responding)
    setTurns({
      responding,
      settled: turns.settled + (responding ? 0 : 1),
    });
  const ready = useBranchServerReady(branch.id);
  const src = getPageUrl(
    space.slug,
    link.route,
    edgeUrlOptions,
    { branchId, externalId },
    customDomain,
    space.customHostname
  );
  return (
    <BrowserChrome
      embedded
      displayUrl={new URL(src).host + navigation.path}
      openUrl={src}
      canBack={navigation.canBack}
      canForward={navigation.canForward}
      onBack={navigation.back}
      onForward={navigation.forward}
      onReload={navigation.reload}
      access={
        <Tooltip
          content={
            auth === liveAuth
              ? routeAuthDescription(auth, groups)
              : `This draft is only visible to members of the workflow’s space. Once pushed live: ${routeAuthDescription(liveAuth, liveGroups)}`
          }
        >
          <AccessIcon aria-label={routeAuthLabel(auth, groups)}>
            <Icon
              name={ROUTE_AUTH_ICONS[auth]}
              size={12}
              color="var(--text-secondary)"
            />
          </AccessIcon>
        </Tooltip>
      }
    >
      {ready ? (
        <Page
          reloadKey={`${turns.settled}:${navigation.reloadToken}`}
          spaceSlug={space.slug}
          spaceId={space.id}
          branchId={branchId}
          edgeRoute={link.route}
          externalId={externalId}
          features={[navigation.feature, agentControl.feature]}
        />
      ) : (
        <EmptyState
          iconName="earth"
          iconSize={24}
          maxWidth={220}
          title="Loading page…"
        />
      )}
    </BrowserChrome>
  );
}

export function WorkflowPagePreview({ link }: { link: WorkflowPageLink }) {
  const edgeUrlOptions = useSessionTenantEdgeUrlOptions();
  const [resolvedLink, setResolvedLink] = useState(link);
  const workflowId =
    link.workflowId ??
    (resolvedLink.href === link.href ? resolvedLink.workflowId : undefined);
  const [branches, details] = useQuery(
    queries.workflowPageForRoute({
      route: link.route,
      ...(link.branchId && { branchId: link.branchId }),
      ...(workflowId && { workflowId }),
    })
  );
  const candidates = branches.filter(
    (branch) =>
      branch.workflow?.space &&
      new URL(
        getPageUrl(branch.workflow.space.slug, link.route, edgeUrlOptions)
      ).origin === link.origin
  );
  // A live address the agent linked before publishing still previews the user’s draft.
  const target =
    candidates.find((branch) => branch.id === link.branchId) ??
    candidates.find((branch) => branch.name === "main") ??
    candidates[0];
  if (target && target.workflowId !== workflowId)
    setResolvedLink({ ...link, workflowId: target.workflowId });
  if (target)
    return <PagePreview key={target.id} branch={target} link={link} />;
  return (
    <EmptyState
      iconName="earth"
      iconSize={24}
      maxWidth={220}
      title={details.type === "complete" ? "Page not found" : "Loading page…"}
    />
  );
}
